import { useMemo } from "react";
import type { TFunction } from "i18next";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import {
  DataTable,
  DataTableSkeleton,
  dataTableFeatures,
} from "@/components/ui/data-table";
import type { Filterdescriptor } from "@/components/ui/data-table-filter";
import { datumKort } from "@/i18n/datum";
import {
  VOORKEUR,
  type Inschrijving,
  type PloegInschrijving,
} from "./useInschrijving";

/**
 * The ploegen this korps has entered, one row each. Picking a row opens the ploeg - the row is
 * the control, which is what `DataTable` is built for, so no cell holds a button of its own.
 */
export function PloegenTabel({
  inschrijving,
  onKies,
}: {
  inschrijving: Inschrijving;
  onKies: (ploegId: string) => void;
}) {
  const { t } = useTranslation("ploegen");

  const helper = createColumnHelper<
    typeof dataTableFeatures,
    PloegInschrijving
  >();

  const columns = helper.columns([
    helper.accessor(
      (ploeg) => `${ploeg.name} ${klassenVan(inschrijving, ploeg)}`,
      {
        id: "ploeg",
        header: t("lijst.ploeg"),
        cell: (info) => (
          <span>
            <span className="font-medium">{info.row.original.name}</span>
            <span className="block text-xs text-muted-foreground">
              {klassenVan(inschrijving, info.row.original) || t("lijst.geenKlasse")}
            </span>
          </span>
        ),
      },
    ),
    helper.accessor((ploeg) => voorkeurenVan(t, inschrijving, ploeg), {
      id: "voorkeuren",
      header: t("lijst.voorkeursdatums"),
      cell: (info) => (
        <span className="text-sm text-muted-foreground">
          {info.getValue() || t("lijst.geenVoorkeuren")}
        </span>
      ),
    }),
  ]);

  // Held whole and local (no `stand`/`onStand`): this table sits inside a korps's own detail
  // pane, not a route of its own, so there is no link worth surviving a reload for.
  const descriptors = useMemo<Filterdescriptor<PloegInschrijving>[]>(
    () => [
      {
        id: "klasse",
        label: t("lijst.filterKlasse"),
        kind: "meerkeuze",
        opties: inschrijving.klassen.map((klasse) => ({
          waarde: klasse.id,
          label: klasse.name,
        })),
        past: (ploeg, waarde) =>
          waarde.soort === "meerkeuze" &&
          waarde.waarden.some((id) => ploeg.klasseIds.includes(id)),
      },
    ],
    [inschrijving.klassen, t],
  );

  return (
    <DataTable
      label={t("lijst.label")}
      data={inschrijving.ploegen}
      columns={columns}
      getRowId={(ploeg) => ploeg.ploegId}
      onSelect={onKies}
      empty={t("lijst.leeg")}
      filters={{ descriptors }}
    />
  );
}

function klassenVan(inschrijving: Inschrijving, ploeg: PloegInschrijving) {
  return inschrijving.klassen
    .filter((klasse) => ploeg.klasseIds.includes(klasse.id))
    .map((klasse) => klasse.name)
    .join(", ");
}

/**
 * The voorkeuren in one line, in rank order, with the "geen voorkeur" marked. Short enough for a
 * cell: at most two dates and one exclusion.
 */
function voorkeurenVan(
  t: TFunction<"ploegen">,
  inschrijving: Inschrijving,
  ploeg: PloegInschrijving,
) {
  const naam = (wedstrijddagId: string) => {
    const dag = inschrijving.wedstrijddagen.find(
      (d) => d.id === wedstrijddagId,
    );
    return dag ? datumKort(dag.date) : t("lijst.onbekendeDag");
  };

  return [...ploeg.voorkeuren]
    .sort((a, b) => a.kind - b.kind || Number(a.rank) - Number(b.rank))
    .map((voorkeur) =>
      voorkeur.kind === VOORKEUR.geenVoorkeur
        ? t("lijst.geenVoorkeurKort", { datum: naam(voorkeur.wedstrijddagId) })
        : t("lijst.voorkeurKort", {
            rang: Number(voorkeur.rank),
            datum: naam(voorkeur.wedstrijddagId),
          }),
    )
    .join(" · ");
}

/** Its own columns, because the real ones close over the inschrijving that is still missing. */
const skeletonKolommen = (t: TFunction<"ploegen">) =>
  createColumnHelper<typeof dataTableFeatures, PloegInschrijving>().columns([
    { id: "ploeg", header: t("lijst.ploeg"), accessorFn: () => "" },
    { id: "voorkeuren", header: t("lijst.voorkeursdatums"), accessorFn: () => "" },
  ]);

export function PloegenTabelSkeleton() {
  const { t } = useTranslation("ploegen");

  return (
    <DataTableSkeleton
      label={t("lijst.label")}
      columns={skeletonKolommen(t)}
      rows={3}
      twoLine={["ploeg"]}
    />
  );
}
